import {
    watch,
    getCurrentScope,
    onScopeDispose,
    type Ref,
    nextTick,
} from 'vue';
import { debounce } from '../../helpers';
import {
    DEFAULT_OPTIONS,
    defaultExtractProcessor,
    defaultSaveProcessor,
    UNAUTHORIZED_USER,
} from './constants';
import type {
    ExtractProcessor,
    Options,
    SaveProcessor,
    UserId,
} from './types';
import {
    buildEnvelopeKey,
    isEnvelope,
    isExpirationEnabled,
    isExpired,
    cleanupExpiredRecordsByMetadata,
    readEnvelope,
    removeEnvelope,
    writeEnvelope,
} from './utils/envelope';
import { buildMetadataKey, updateRecordInRegistry } from './utils/feature-metadata';
import { isAuthorizedUserId } from './utils/user-id';
import { resolveStorageArea } from './utils/storage-api-utils';

/**
 * Синхронизирует реактивный `Ref` с `localStorage` или `sessionStorage` для конкретного пользователя.
 *
 * @param storageName - Уникальное имя хранилища фичи (уникальная константа для каждого конкретного потребителя).
 * @param userId - Идентификатор пользователя. `null`, `undefined` и неавторизованные значения
 *   сохраняются под служебным ключом гостя.
 * @param storage - Реактивная ссылка, которую нужно синхронизировать с хранилищем.
 * @param options - Кофигурация работы composable:
 * @param options.storage - Тип хранилища. `STORAGE_LOCAL` — `localStorage`, `STORAGE_SESSION` — `sessionStorage`.
 * @param options.ttlMs - Срок годности данных в миллисекундах. Проверяется индивидуально для каждого ключа
 *   (пользователя). Значение `0` отключает устаревание.
 * @param options.saveDebounceMs - Задержка (мс) перед записью изменений в хранилище.
 * @param options.cleanupOnStartup - При каждом старте composable проверяет все записи фичи на протухание
 *   и удаляет их. Предотвращает переполнение хранилища.
 * @param options.renewOnInitialRead - Продлевает TTL при стартовом чтении пользовательских данных.
 * @param options.renewOnMigrationFromUnauthorized - Автоматически продлевает TTL при миграции гостевых данных
 *   в данные авторизованного пользователя.
 * @param options.syncByStorageSignal - **[EXPERIMENTAL]** Подписывается на событие `storage` и актуализирует данные
 *   при изменении хранилища из другой вкладки. Возможны race condition из-за debounce при записи.
 * @param extractProcessor - Преобразует данные из хранилища в значение для рефки `storage`.
 * @param saveProcessor - Преобразует значение рефки `storage` перед записью в хранилище.
 * @returns Объект с методом `dispose` для ручной отписки.
 */
export const useStorageSync = <T>(
    storageName: string,
    userId: UserId,
    storage: Ref<T>,
    options: Partial<Options> = {},
    extractProcessor: ExtractProcessor<T> = defaultExtractProcessor,
    saveProcessor: SaveProcessor<T> = defaultSaveProcessor
) => {
    const resolvedOptions: Options = {
        ...DEFAULT_OPTIONS,
        ...options,
    };

    const browserStorage = resolveStorageArea(resolvedOptions.storage);
    const envelopeKey = buildEnvelopeKey(storageName, userId);
    const metadataKey = buildMetadataKey(storageName);

    const writeEnvelopeDebounced = debounce(
        (data: unknown, savedAt: number = Date.now()) => {
            writeEnvelope(envelopeKey, metadataKey, data, savedAt, browserStorage);
        },
        resolvedOptions.saveDebounceMs
    );

    // счетчик для подавления watch-триггера при программной перезаписи storage
    // init не подавляем — watch ещё не создан
    let suppressWatchCount = 0;

    const applyExtractedValue = async (value: T) => {
        // это нужно только для кейса с синхронизацией нескольких табов
        // если в текущем табе уже запущен дебаунс,
        // а из другого таба прилетели изменения, и синхронно обновили данные
        // то запущенный дебаунс их затрет
        // НО:
        // tут хз как правильнее, гонка состояний
        // + он будет отменять любые дебаунсы, даже если они корректные - сложно спрогнозировать
        writeEnvelopeDebounced.cancel();

        suppressWatchCount++;

        try {
            storage.value = value;
            await nextTick();
        }
        finally {
            suppressWatchCount--;
        }
    };

    const migrateDataFromUnauthorized = () => {
        const unauthorizedEnvelopeKey = buildEnvelopeKey(storageName, UNAUTHORIZED_USER);

        // ультрапограничный случай:
        // если в "userId" передали значение равной константе UNAUTHORIZED_USER
        // не мигрировать в себя
        if (unauthorizedEnvelopeKey === envelopeKey) return;

        const unauthorizedEnvelope = readEnvelope(
            unauthorizedEnvelopeKey,
            metadataKey,
            resolvedOptions.ttlMs,
            browserStorage
        );

        if (unauthorizedEnvelope) {
            const extractedPayload = extractProcessor(unauthorizedEnvelope.data);

            if (resolvedOptions.renewOnMigrationFromUnauthorized) {
                // пересобираем Envelope, чтобы обновить время
                writeEnvelope(envelopeKey, metadataKey, saveProcessor(extractedPayload), Date.now(), browserStorage);
            }
            else {
                // сохраняем как есть, со старым временем
                writeEnvelope(
                    envelopeKey,
                    metadataKey,
                    unauthorizedEnvelope.data,
                    unauthorizedEnvelope.savedAt,
                    browserStorage
                );
            }
            // watch ещё не создан, suppress не нужен — пишем напрямую, как при init
            storage.value = extractedPayload;
            removeEnvelope(unauthorizedEnvelopeKey, metadataKey, browserStorage);
        }
    };

    //
    // init / original sync
    //

    if (resolvedOptions.cleanupOnStartup && isExpirationEnabled(resolvedOptions.ttlMs)) {
        cleanupExpiredRecordsByMetadata(metadataKey, resolvedOptions.ttlMs, browserStorage);
    }

    const envelope = readEnvelope(envelopeKey, metadataKey, resolvedOptions.ttlMs, browserStorage);

    if (envelope) {
        // здесь не нужен applyExtractedValue, t.к. watch еще не создан, и его не надо прерывать
        storage.value = extractProcessor(envelope.data);

        // синхронизируем реестр для записей, созданных до появления metadata.registry
        updateRecordInRegistry(metadataKey, envelopeKey, envelope.savedAt, browserStorage);

        // обновляем время, если настройка включена
        if (resolvedOptions.renewOnInitialRead) {
            writeEnvelope(envelopeKey, metadataKey, envelope.data, Date.now(), browserStorage);
        }
    }
    else if (isAuthorizedUserId(userId)) {
        migrateDataFromUnauthorized();
    }

    //
    // reactive live sync
    //

    const stopWatching = watch(
        storage,
        (newValue) => {
            if (suppressWatchCount > 0) return;

            writeEnvelopeDebounced(saveProcessor(newValue));
        },
        {
            deep: true,
        }
    );

    //
    // cross-tab sync
    //

    const onStorageEvent = (e: StorageEvent) => {
        if (e.storageArea !== browserStorage) return;
        if (e.key !== envelopeKey || e.newValue === null) return;

        try {
            const parsed = JSON.parse(e.newValue);

            if (!isEnvelope(parsed)) return;
            if (isExpired(parsed.savedAt, resolvedOptions.ttlMs)) {
                removeEnvelope(envelopeKey, metadataKey, browserStorage);
                return;
            }

            updateRecordInRegistry(metadataKey, envelopeKey, parsed.savedAt, browserStorage);

            void applyExtractedValue(extractProcessor(parsed.data));
        } catch (err) {
            console.error(err);
        }
    };

    if (resolvedOptions.syncByStorageSignal) {
        window.addEventListener('storage', onStorageEvent);
    }

    //
    // auto unmount/dispose
    //

    // 1. если вызван внутри scope (компонента/effectScope) — снимаем DOM-listener при dispose
    // 2. если вне scope - потребитель должен вызвать коллбэк сам
    const dispose = () => {
        stopWatching();

        if (resolvedOptions.syncByStorageSignal) {
            window.removeEventListener('storage', onStorageEvent);
        }

        // дописываем последнее изменение синхронно, иначе оно теряется при размонтировании в окне дебаунса
        if (writeEnvelopeDebounced.isWaiting()) {
            writeEnvelopeDebounced.cancel();
            writeEnvelope(envelopeKey, metadataKey, saveProcessor(storage.value), Date.now(), browserStorage);
        }
    };

    if (getCurrentScope()) {
        onScopeDispose(dispose);
    }

    return {
        dispose,
    };
};
